import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Upload,
  Form,
  message,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import fileToBase64 from 'utils/fileToBase64';

const divideSize = (size, by = 2) => {
  const number = parseFloat(size);
  return (number / by).toString().concat(size.replace(number, ''));
};

const AvatarUploader = ({
  disabled,
  formItemName,
  formItemLabel,
  size,
  shape,
  aspectRatio,
  onRemove,
  rules,
}) => {
  const form = Form.useFormInstance();
  const formItemValue = Form.useWatch(formItemName, form);
  const [image, setImage] = useState();
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    const setBase64 = async () => {
      const base64 = await fileToBase64(formItemValue.file);
      setImage(base64);
    };

    if (typeof formItemValue === 'string' || !formItemValue) {
      setImage(formItemValue);
    } else {
      setBase64();
    }
    setFileList([formItemValue?.file].filter((e) => e));
  }, [formItemValue]);

  return (
    <FormItemUploader
      label={formItemLabel}
      style={{ marginBottom: 0 }}
      name={formItemName}
      size={typeof size === 'number' ? `${size}px` : size}
      radius={shape === 'circle' ? '50%' : '8px'}
      shape={shape}
      height={divideSize(typeof size === 'number' ? `${size}px` : size, aspectRatio)}
      rules={rules}
    >
      <Upload
        name="avatar"
        className="avatar-uploader"
        accept="image/png, image/jpeg, image/jpg"
        onRemove={() => {
          form.resetFields([formItemName]);
          if (onRemove) onRemove();
        }}
        onChange={(info) => {
          const { file } = info;
          const ext = file.name.split('.').pop();

          const isValidExt = ext === 'jpg' || ext === 'jpeg' || ext === 'png';
          if (!isValidExt) {
            message.warn('Solo se admiten archivos en formato JPG, JPEG o PNG');
          }

          const isLt2M = file.size / 1024 / 1024 < 2;
          if (!isLt2M) {
            message.warn('El archivo no debe sobrepasar los 2MB');
          }
          if (!(isValidExt && isLt2M)) {
            form.resetFields([formItemName]);
          }
        }}
        listType="picture-card"
        beforeUpload={() => false}
        showUploadList={false}
        disabled={disabled}
        fileList={fileList}
      >
        {image
          ? (
            <div className="preview">
              <img src={image} alt="avatar" style={{ width: '100%' }} />
              { /* eslint-disable-next-line */}
              <div
                className="remove"
                onClick={(e) => {
                  e.stopPropagation();
                  form.resetFields([formItemName]);
                }}
              >
                <Typography.Text>
                  Eliminar
                </Typography.Text>
                <DeleteOutlined />
              </div>
              <div className="edit">
                <Typography.Text>
                  Editar
                </Typography.Text>
                <EditOutlined />
              </div>
            </div>
          )
          : (
            <div>
              <PlusOutlined />
              <div className="ant-upload-text">Subir</div>
            </div>
          )}
      </Upload>
    </FormItemUploader>
  );
};

AvatarUploader.propTypes = {
  disabled: PropTypes.bool,
  formItemName: PropTypes.string,
  formItemLabel: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shape: PropTypes.oneOf(['circle', 'square']),
  aspectRatio: PropTypes.number,
  onRemove: PropTypes.func,
  rules: PropTypes.arrayOf(PropTypes.shape({})),
};

AvatarUploader.defaultProps = {
  disabled: false,
  formItemName: 'foto',
  formItemLabel: 'Fotografía',
  size: 128,
  shape: 'circle',
  aspectRatio: 1,
  onRemove: null,
  rules: [],
};

const FormItemUploader = styled(Form.Item)`
&, .avatar-uploader>.ant-upload {
  width: ${(props) => props.size};
  height: ${(props) => props.height};
}

.avatar-uploader>.ant-upload {
  border-radius: ${(props) => props.radius};

  .preview {
    div {
      display: none;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      background-color: #00000035;
      position: absolute;
      width: ${(props) => props.size};
      height: ${(props) => divideSize(props.height)};
    }

    img {
      width: ${(props) => props.size} !important;
      height: ${(props) => props.height} !important;
      border-radius: ${(props) => props.radius};
      border: 1px solid #00000073;
      object-fit: cover;
    }

    .anticon {
      font-size: 18px;
      /* font-size: min(18px, ${(props) => divideSize(props.size, 6)}); */
    }
    .ant-typography {
      font-size: 18px;
      color: #FFFFFF;
      margin-right: 5px;
    }

    .edit {
      top: 0px;
      ${({ shape, radius }) => shape === 'square' && `
        border-top-left-radius: ${radius};
        border-top-right-radius: ${radius};
      `}
      ${({ shape, size }) => shape === 'circle' && `
        border-top-left-radius: ${divideSize(size)};
        border-top-right-radius: ${divideSize(size)};
      `}
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      .anticon {
        color: #005eff;
      }

      &:hover {
        background-color: #005eff79;
        .anticon {
          color: #FFFFFF !important;
        }
      }
    }

    .remove {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      top: ${(props) => divideSize(props.height)};
      ${({ shape, radius }) => shape === 'square' && `
        border-bottom-left-radius: ${radius};
        border-bottom-right-radius: ${radius};
      `}
      ${({ shape, size }) => shape === 'circle' && `
        border-bottom-left-radius: ${divideSize(size)};
        border-bottom-right-radius: ${divideSize(size)};
      `}

      .anticon {
        color: #ff4d4f;
      }

      &:hover {
        background-color: #ff4d4fC0;
        .anticon {
          color: #FFFFFF !important;
        }
      }
    }
  }

  &:not(.ant-upload-disabled) .preview:hover div {
    display: flex;
  }
}

.ant-form-item-control-input-content {
  display: flex;
  flex-direction: row;
  justify-content: center;

  .ant-upload-picture-card-wrapper {
    width: auto !important;
  }
}
`;

export default AvatarUploader;
